import { useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';

import type { BoardCard, BoardColumn, BoardWaitingGroup, UiAlert } from '@nightshift/ui-types';

import { BoardSelectionContext } from '../components/board-selection-context.ts';
import { selectBoardCard, type BoardSelection } from '../lib/board-selection.ts';
import { BoardCardView } from '../components/board-card.tsx';
import { Empty, Spinner, StateDot } from '../components/primitives.tsx';
import { api, type SessionUser } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import {
  BOARD_HOLD_REASON,
  boardDropCommand,
  dropTargetAccepts,
  isRefiningActivity,
  type BoardDragPayload,
  type BoardDropTarget,
} from '../lib/board-drag.ts';
import { emptyLine } from '../lib/board-empty.ts';
import {
  reconcileCollapsedColumns,
  sortBacklogCards,
  type BoardSort,
  type BoardView as BoardViewState,
} from '../lib/board-view.ts';
import { useTimeFormat } from '../lib/display.ts';
import { observedTime } from '../lib/format.ts';
import { stateStyle, type TicketState } from '../lib/palette.ts';
import { ticketsIndexHref } from '../lib/tickets-view.ts';
import {
  CubeIcon,
  HeartbeatIcon,
  HourglassIcon,
  RefreshIcon,
  RocketIcon,
  UsersThreeIcon,
} from '../shell/icons.tsx';
import './board.css';

export interface ViewProps {
  onOpenTicket: (key: string, run?: number) => void;
  onOpenAlert: (alert: UiAlert) => void;
}

export interface BoardViewProps extends ViewProps {
  view: BoardViewState;
  onChangeView: (next: BoardViewState) => void;
  onOpenTicketsIndex: (filter: { state: string; reason?: string }) => void;
  user: SessionUser | null;
}

/**
 * The one structured term the board filter understands.
 *
 * A token rather than a checkbox because the filter already lives in the URL, so a board
 * narrowed to open questions is a link somebody can paste. Everything else the box does is
 * substring matching, and a card that literally contains the word "suggested" in its title
 * should not answer this query, so the token is stripped before the substring pass.
 */
const SUGGESTED_FILTER_TOKEN = 'is:suggested';

function matchesFilter(card: BoardCard, needle: string): boolean {
  const wantsSuggested = needle.includes(SUGGESTED_FILTER_TOKEN);
  if (wantsSuggested && card.suggestedCount === 0) return false;
  const rest = wantsSuggested ? needle.split(SUGGESTED_FILTER_TOKEN).join(' ').trim() : needle;
  if (rest.length === 0) return true;
  return (
    card.ticketKey.toLowerCase().includes(rest) ||
    card.title.toLowerCase().includes(rest) ||
    (card.waitingReasonText ?? '').toLowerCase().includes(rest)
  );
}

/**
 * Dark Factory board — columns from GET /api/board.
 * Drag issues ticket commands (SPEC.md decision 19); it does not write state.
 *
 * Layout contract: docs/ui/dark-factory-board-mock.png and SPEC.md decision 18.
 * Columns list every card and scroll in `.col-body` — no click-to-expand.
 * Visual world: Attenuated (DESIGN.md). Per-state extras that are still null stay omitted.
 */
export function BoardView({
  onOpenTicket,
  view,
  onChangeView,
  onOpenAlert: _onOpenAlert,
  onOpenTicketsIndex,
  user,
}: BoardViewProps) {
  const state = useLive();
  const store = useStore();
  const [dragging, setDragging] = useState<BoardDragPayload | null>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(() => new Set());
  const previousColumnCounts = useRef<ReadonlyMap<string, number>>(new Map());
  const previousProject = useRef(state.projectSlug);
  const draggingRef = useRef<BoardDragPayload | null>(null);
  const setDragPayload = (payload: BoardDragPayload | null): void => {
    draggingRef.current = payload;
    setDragging(payload);
  };
  const [selection, setSelection] = useState<BoardSelection>({ keys: [], anchor: null });
  useLayoutEffect(() => {
    setSelection({ keys: [], anchor: null });
  }, [state.projectSlug, state.milestoneId, state.phaseId, view.filterText, view.sortBy]);
  const board = state.board;
  const now = state.tick;
  const needle = view.filterText.trim().toLowerCase();
  const milestone = state.milestoneId == null ? null : String(state.milestoneId);
  const phase = state.phaseId == null ? null : String(state.phaseId);

  // Reconcile before paint so an empty project does not flash eight full-width columns while
  // its initial automatic collapse catches up.
  useLayoutEffect(() => {
    if (!board) return;
    const projectChanged = previousProject.current !== state.projectSlug;
    const previous = projectChanged ? new Map<string, number>() : previousColumnCounts.current;
    setCollapsedColumns((current) =>
      reconcileCollapsedColumns(previous, board.columns, projectChanged ? new Set() : current),
    );
    previousColumnCounts.current = new Map(board.columns.map((column) => [column.key, column.count]));
    previousProject.current = state.projectSlug;
  }, [board, state.projectSlug]);

  const commitDrop = async (target: BoardDropTarget): Promise<void> => {
    const current = draggingRef.current;
    if (!current) return;
    const tickets = current.tickets ?? [current];
    if (!tickets.every((ticket) => boardDropCommand(ticket.fromState, target, { resumeState: ticket.resumeState }))) {
      setDragPayload(null);
      store.toast('bad', 'That is not a legal move for every selected ticket.');
      return;
    }
    setDragPayload(null);
    try {
      const results = await Promise.allSettled(tickets.map(async (ticket) => {
        const key = ticket.ticketKey;
        const command = boardDropCommand(ticket.fromState, target, { resumeState: ticket.resumeState })!;
        const result =
          command.type === 'start'
            ? await api.start(key, command.activity ? { activity: command.activity } : {}).then((res) => ({
                ...res,
                // Say which Refining lane the ticket started in. The server message alone left
                // a ticket dropped on the wrong group looking like it had started correctly.
                message: `${key}: ${res.message ?? 'Started.'}${command.activity ? ` Activity: ${command.activity}.` : ''}`,
              }))
            : command.type === 'record_done'
              ? await api.recordDone(key)
              : command.type === 'shelve'
                ? await api.shelve(key)
                : command.type === 'hold'
                  ? await api.wait(key, { reason_type: 'manual_hold', reason: BOARD_HOLD_REASON })
                  : command.type === 'cancel'
                    ? await api.cancel(key)
                    : command.type === 'restore'
                      ? await api.restore(key)
                      : await api.resume(key);
        return { ...result, message: result.message ?? `${key}: ${command.type} completed.` };
      }));
      for (const result of results) {
        if (result.status === 'fulfilled') store.toast('good', result.value.message);
        else store.toast('bad', String(result.reason));
      }
      await store.refetch();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    }
  };

  if (state.boardError && state.boardError !== 'session-expired') {
    return (
      <Empty
        title="The board could not be read."
        body={state.boardError}
      />
    );
  }

  // A member whose GitLab account reaches nothing gets an explanation rather than a board
  // with no columns, which otherwise reads as a broken dashboard rather than a permission.
  if (state.snapshot && state.snapshot.projects.length === 0 && user?.role === 'member') {
    return (
      <Empty
        title="No projects yet."
        body="You see a project once your GitLab account can read it. Ask an admin to add you to the project's group in GitLab, or to link your GitLab account in Settings."
      />
    );
  }

  if (!board) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
        <Spinner size={20} />
      </div>
    );
  }

  const filterCards = (cards: readonly BoardCard[]): BoardCard[] =>
    needle.length === 0 ? [...cards] : cards.filter((card) => matchesFilter(card, needle));

  const selectedCards = [...board.columns.flatMap((column) => column.cards), ...board.waiting.groups.flatMap((group) => group.cards)]
    .filter((card) => selection.keys.includes(card.ticketKey));
  return (
    <BoardSelectionContext.Provider value={{ cards: selectedCards, select: (card, stack, event) => {
      setSelection((current) => selectBoardCard(current, card.ticketKey, stack.map((entry) => entry.ticketKey), event));
    } }}>
    <div className="df-board">
      <div className="df-board-row">
        <div className="df-columns" role="list">
          {board.columns.map((column) => {
            const visibleCards = filterCards(column.cards);
            return <PrimaryColumn
              key={column.key}
              column={column}
              collapsed={collapsedColumns.has(column.key)}
              onToggleCollapsed={() => {
                setSelection({ keys: [], anchor: null });
                setCollapsedColumns((current) => {
                  const next = new Set(current);
                  if (next.has(column.key)) next.delete(column.key);
                  else next.add(column.key);
                  return next;
                });
              }}
              cards={column.key === 'Backlog' ? sortBacklogCards(visibleCards, view.sortBy) : visibleCards}
              hiddenCount={column.cards.length - visibleCards.length}
              filterActive={needle.length > 0}
              generatedAt={board.generatedAt}
              now={now}
              onOpen={onOpenTicket}
              dragging={dragging}
              onDragPayload={setDragPayload}
              onDropTarget={commitDrop}
              user={user}
              deployConfigured={board.deployConfigured}
              onFilterSuggested={() =>
                onChangeView({ ...view, filterText: SUGGESTED_FILTER_TOKEN })
              }
              sortBy={view.sortBy}
              onSort={(sortBy) => onChangeView({ ...view, sortBy })}
            />;
          })}
        </div>
        <WaitingPane
          groups={board.waiting.groups}
          count={board.waiting.count}
          cancelledCount={board.cancelled.count}
          generatedAt={board.generatedAt}
          now={now}
          onOpenTicket={onOpenTicket}
          onOpenTicketsIndex={onOpenTicketsIndex}
          ticketsHref={(opts) =>
            ticketsIndexHref({
              project: state.projectSlug,
              milestone,
              phase,
              state: opts.state,
              reason: opts.reason,
            })
          }
          dragging={dragging}
          onDragPayload={setDragPayload}
          onDropTarget={commitDrop}
          user={user}
          deployConfigured={board.deployConfigured}
        />
      </div>
      <StatusStrip />
    </div>
    </BoardSelectionContext.Provider>
  );
}

function dropHandlers(
  dragging: BoardDragPayload | null,
  target: BoardDropTarget,
  onDropTarget: (target: BoardDropTarget) => void,
) {
  const accepts = dragging ? (dragging.tickets ?? [dragging]).every((ticket) => dropTargetAccepts(ticket.fromState, target, { resumeState: ticket.resumeState })) : false;
  return {
    accepts,
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!dragging) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = accepts ? 'move' : 'none';
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      if (accepts) onDropTarget(target);
    },
  };
}

function PrimaryColumn({
  column,
  collapsed,
  onToggleCollapsed,
  cards,
  hiddenCount,
  filterActive,
  generatedAt,
  now,
  onOpen,
  dragging,
  onDragPayload,
  onDropTarget,
  user,
  deployConfigured,
  onFilterSuggested,
  sortBy,
  onSort,
}: {
  column: BoardColumn;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  cards: BoardCard[];
  hiddenCount: number;
  filterActive: boolean;
  onFilterSuggested?: () => void;
  sortBy: BoardSort;
  onSort: (sortBy: BoardSort) => void;
  generatedAt: number;
  now: number;
  onOpen: (key: string) => void;
  dragging: BoardDragPayload | null;
  onDragPayload: (payload: BoardDragPayload | null) => void;
  onDropTarget: (target: BoardDropTarget) => void;
  user: SessionUser | null;
  deployConfigured: boolean;
}) {
  const style = stateStyle(column.key as TicketState);
  const human = column.key === 'Human Review' && cards.length > 0;
  // Counted over the whole column rather than the filtered cards: the point of the number is
  // to say how much is waiting on a person, which a filter must not be able to talk down.
  const suggestedCount = column.cards.filter((card) => card.suggestedCount > 0).length;
  const columnTarget: BoardDropTarget = { kind: 'column', key: column.key as TicketState };
  const drop = dropHandlers(dragging, columnTarget, onDropTarget);
  // A legal drag target opens temporarily so moving a ticket into an empty state never
  // requires a separate click. The stored choice is untouched and population takes over once
  // the server confirms the move.
  const visuallyCollapsed = collapsed && !(dragging && drop.accepts);

  // Collapsing replaces the header's toggle with the spine button and expanding does the
  // reverse, so the element the operator just activated leaves the document and focus falls
  // back to the body: a keyboard operator lands at the top of the page and tabs all the way
  // back. Only a click moves focus on -- a column that opens because a drag is passing over it
  // must not steal it.
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const refocus = useRef(false);
  useLayoutEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    toggleRef.current?.focus();
  }, [visuallyCollapsed]);
  const toggleCollapsed = (): void => {
    refocus.current = true;
    onToggleCollapsed();
  };

  return (
    <section
      className={`col df-col${visuallyCollapsed ? ' col-collapsed' : ''}${human ? ' col-human' : ''}${dragging && drop.accepts ? ' is-drop-ok' : ''}${dragging && !drop.accepts ? ' is-drop-no' : ''}`}
      style={human ? { background: style.wash, boxShadow: `inset 0 0 0 1px ${style.edge}` } : undefined}
      aria-label={`${column.key} — ${column.count} tickets`}
      role="listitem"
      onDragOver={drop.onDragOver}
      onDrop={drop.onDrop}
    >
      {visuallyCollapsed ? (
        <button
          type="button"
          ref={toggleRef}
          className="col-spine"
          aria-expanded="false"
          aria-label={`Expand ${column.key}, ${column.count} ${column.count === 1 ? 'ticket' : 'tickets'}`}
          title={`Expand ${column.key}`}
          onClick={toggleCollapsed}
        >
          <StateDot state={column.key as TicketState} />
          <span className="col-vert">{column.key}</span>
          <span className="col-collapsed-count num">{column.count}</span>
          <ColumnCaret direction="right" />
        </button>
      ) : (
        <>
          <header className={`col-head${column.key === 'Backlog' ? ' col-head-sortable' : ''}`}>
            <StateDot state={column.key as TicketState} />
            <span className="col-name">{column.key}</span>
            <span className="col-count num">{column.count}</span>
            <button
              type="button"
              ref={toggleRef}
              className="col-toggle"
              aria-expanded="true"
              aria-label={`Collapse ${column.key}`}
              title={`Collapse ${column.key}`}
              onClick={toggleCollapsed}
            >
              <ColumnCaret direction="left" />
            </button>
            {column.key === 'Backlog' ? (
              <label className="col-sort-label">
                <span className="visually-hidden">Sort Backlog</span>
                <select
                  className="col-sort"
                  aria-label="Sort Backlog"
                  value={sortBy}
                  onChange={(event) => onSort(event.target.value as BoardSort)}
                >
                  <option value="dependency">Dependency</option>
                  <option value="updated">Updated</option>
                  <option value="key">Key</option>
                </select>
              </label>
            ) : null}
            {suggestedCount > 0 && onFilterSuggested ? (
              <button
                type="button"
                className="col-suggested"
                title="Show only tickets with a dependency decision to make"
                onClick={() => onFilterSuggested()}
              >
                {suggestedCount} {suggestedCount === 1 ? 'needs' : 'need'} attention
              </button>
            ) : null}
              </header>
              <div className="col-body">
            {column.key === 'Refining' && column.groups ? (
              column.groups.map((group) => {
                const groupCards = filterActive
                  ? group.cards.filter((card) => cards.some((kept) => kept.id === card.id))
                  : group.cards;
                const groupTarget: BoardDropTarget = {
                  kind: 'column',
                  key: 'Refining',
                  ...(isRefiningActivity(group.key) ? { activity: group.key } : {}),
                };
                const groupDrop = dropHandlers(dragging, groupTarget, onDropTarget);
                return (
                  <div
                    key={group.key}
                    className={`col-group${dragging && groupDrop.accepts ? ' is-drop-ok' : ''}`}
                    onDragOver={groupDrop.onDragOver}
                    onDrop={(event) => {
                      event.stopPropagation();
                      groupDrop.onDrop(event);
                    }}
                  >
                    <div className="col-group-head">
                      <span className="col-group-label">{group.label}</span>
                      <span className="col-group-count num">{group.count}</span>
                      {dragging && groupDrop.accepts ? (
                        // Names the activity the drop will start. The groups fill the column, so
                        // a card let go "in Refining" lands in whichever group is under the cursor;
                        // a ticket meant for Standard used to start in Research without anyone
                        // seeing why.
                        <span className="col-group-drop-hint">Drop to start in {group.label}</span>
                      ) : null}
                    </div>
                    {groupCards.length === 0 ? (
                      <p className="col-empty col-empty-group">None</p>
                    ) : (
                      groupCards.map((card) => (
                        <BoardCardView
                          key={card.id}
                          card={card}
                          stack={groupCards}
                          generatedAt={generatedAt}
                          now={now}
                          onOpen={onOpen}
                          draggingKey={dragging?.ticketKey ?? null}
                          onDragPayload={onDragPayload}
                          user={user}
                          deployConfigured={deployConfigured}
                        />
                      ))
                    )}
                  </div>
                );
              })
            ) : cards.length === 0 ? (
              <p className="col-empty">
                {emptyLine(column.key as TicketState, { filterActive, hiddenCount })}
              </p>
            ) : (
              cards.map((card) => (
                <BoardCardView
                  key={card.id}
                  card={card}
                  stack={cards}
                  generatedAt={generatedAt}
                  now={now}
                  onOpen={onOpen}
                  draggingKey={dragging?.ticketKey ?? null}
                  onDragPayload={onDragPayload}
                  user={user}
                  deployConfigured={deployConfigured}
                />
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ColumnCaret({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      className="col-toggle-icon"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden
    >
      <path
        d={direction === 'left' ? 'M6.5 1.5 L2.5 5 L6.5 8.5' : 'M3.5 1.5 L7.5 5 L3.5 8.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WaitingPane({
  groups,
  count,
  cancelledCount,
  generatedAt,
  now,
  onOpenTicket,
  onOpenTicketsIndex,
  ticketsHref,
  dragging,
  onDragPayload,
  onDropTarget,
  user,
  deployConfigured,
}: {
  groups: BoardWaitingGroup[];
  count: number;
  cancelledCount: number;
  generatedAt: number;
  now: number;
  onOpenTicket: (key: string, run?: number) => void;
  onOpenTicketsIndex: (filter: { state: string; reason?: string }) => void;
  ticketsHref: (filter: { state: string; reason?: string }) => string;
  dragging: BoardDragPayload | null;
  onDragPayload: (payload: BoardDragPayload | null) => void;
  onDropTarget: (target: BoardDropTarget) => void;
  user: SessionUser | null;
  deployConfigured: boolean;
}) {
  const waitingStyle = stateStyle('Waiting');
  const holdTarget: BoardDropTarget = { kind: 'waiting', reason: 'manual_hold' };
  const holdDrop = dropHandlers(dragging, holdTarget, onDropTarget);
  const cancelTarget: BoardDropTarget = { kind: 'cancelled' };
  const cancelDrop = dropHandlers(dragging, cancelTarget, onDropTarget);
  // Only the rows the reader has toggled by hand. Everything else follows its population,
  // so a reason that fills while the board is on screen opens itself and one that empties
  // gets out of the way — without overriding a deliberate collapse.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  return (
    <aside
      className={`df-waiting${dragging && holdDrop.accepts ? ' is-drop-ok' : ''}`}
      aria-label={`Waiting — ${count}`}
      onDragOver={holdDrop.onDragOver}
      onDrop={holdDrop.onDrop}
    >
      <header className="col-head">
        <StateDot state="Waiting" pulse={count > 0} />
        <span className="col-name" style={{ color: waitingStyle.text }}>
          Waiting
        </span>
        <span className="col-count num" style={{ color: waitingStyle.text }}>
          {count}
        </span>
      </header>
      <ul className="df-waiting-list">
        {groups.map((group) => {
          // Populated rows wear a colour: amber for the ones that want a person, indigo for the
          // queue, which wants nobody. Both still open by default -- what is parked is worth
          // seeing either way.
          const hot = group.count > 0;
          const queued = group.key === 'dependency';
          const open = toggled[group.key] ?? hot;
          const bodyId = `df-waiting-cards-${group.key}`;
          const href = ticketsHref({ state: 'Waiting', reason: group.key });
          const rowTarget: BoardDropTarget = { kind: 'waiting', reason: group.key };
          const rowDrop = dropHandlers(dragging, rowTarget, onDropTarget);
          return (
            <li
              key={group.key}
              // The whole group is the drop target for its reason, so a card let go over an
              // open group's cards parks for that reason instead of falling through to Hold.
              className={`df-waiting-group${dragging && rowDrop.accepts ? ' is-drop-ok' : ''}`}
              onDragOver={(event) => {
                event.stopPropagation();
                rowDrop.onDragOver(event);
              }}
              onDrop={(event) => {
                event.stopPropagation();
                rowDrop.onDrop(event);
              }}
            >
              <button
                type="button"
                className={`df-waiting-row${hot ? (queued ? ' is-queued' : ' is-hot') : ''}${open ? ' is-open' : ''}`}
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={() => setToggled((prev) => ({ ...prev, [group.key]: !open }))}
              >
                <WaitingCaret open={open} />
                <span className="df-waiting-label">{group.label}</span>
                <span className="df-waiting-count num">{group.count}</span>
              </button>
              {open ? (
                <div className="df-waiting-cards" id={bodyId}>
                  {hot ? (
                    <>
                      {group.cards.map((card) => (
                        <BoardCardView
                          key={card.id}
                          card={card}
                          stack={group.cards}
                          generatedAt={generatedAt}
                          now={now}
                          onOpen={onOpenTicket}
                          draggingKey={dragging?.ticketKey ?? null}
                          onDragPayload={onDragPayload}
                          user={user}
                          deployConfigured={deployConfigured}
                        />
                      ))}
                      <a
                        className="df-waiting-all"
                        href={href}
                        onClick={(event) => {
                          if (
                            event.defaultPrevented ||
                            event.metaKey ||
                            event.ctrlKey ||
                            event.shiftKey ||
                            event.altKey
                          ) {
                            return;
                          }
                          event.preventDefault();
                          onOpenTicketsIndex({ state: 'Waiting', reason: group.key });
                        }}
                      >
                        Open in Tickets
                      </a>
                    </>
                  ) : (
                    <p className="df-waiting-none">Nothing parked here.</p>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div
        className={`df-cancelled${dragging && cancelDrop.accepts ? ' is-drop-ok' : ''}`}
        onDragOver={(event) => {
          event.stopPropagation();
          cancelDrop.onDragOver(event);
        }}
        onDrop={(event) => {
          event.stopPropagation();
          cancelDrop.onDrop(event);
        }}
      >
        <a
          className="df-cancelled-rail"
          href={ticketsHref({ state: 'Cancelled' })}
          onClick={(event) => {
            if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
              return;
            }
            event.preventDefault();
            onOpenTicketsIndex({ state: 'Cancelled' });
          }}
        >
          <StateDot state="Cancelled" />
          <span>Cancelled</span>
          <span className="num">{cancelledCount}</span>
        </a>
      </div>
    </aside>
  );
}

/** Disclosure chevron for a waiting group. Points right when shut, down when open. */
function WaitingCaret({ open }: { open: boolean }) {
  return (
    <svg
      className={`df-waiting-caret${open ? ' is-open' : ''}`}
      width="9"
      height="9"
      viewBox="0 0 10 10"
      aria-hidden
    >
      <path
        d="M3 1.5 L7 5 L3 8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StatusStrip() {
  const state = useLive();
  const format = useTimeFormat();
  const board = state.board;
  const snapshot = state.snapshot;
  if (!board) return null;

  const pipelineActive = snapshot?.health.pipelineActive;
  const healthLabel =
    pipelineActive === true
      ? 'All systems operational'
      : pipelineActive === false
        ? 'Adapters off'
        : 'Health unknown';
  const healthTone = pipelineActive === true ? 'good' : pipelineActive === false ? 'warn' : 'dim';
  const agents = board.summary.activeAgents;
  const ci = board.summary.ciPipelines;
  const waiting = board.summary.waitingNeedsAttention;

  return (
    <footer className="df-strip" aria-label="Board status">
      <StripCell
        icon={<HeartbeatIcon size={13} />}
        label="System Health"
        value={healthLabel}
        tone={healthTone}
      />
      <StripCell
        icon={<UsersThreeIcon size={13} />}
        label="Active Agents"
        value={agents === 0 ? '0 idle' : `${agents} running`}
      />
      <StripCell
        icon={<CubeIcon size={13} />}
        label="CI Pipelines"
        value={ci === 0 ? '0 idle' : `${ci} running`}
      />
      <StripCell
        icon={<RocketIcon size={13} />}
        label="Tickets in Flight"
        value={`${board.summary.inFlight} across workflow`}
      />
      <StripCell
        icon={<HourglassIcon size={13} />}
        label="Waiting"
        value={`${waiting} ${waiting === 1 ? 'needs attention' : 'need attention'}`}
        tone={waiting > 0 ? 'warn' : 'dim'}
      />
      <StripCell
        icon={<RefreshIcon size={13} />}
        label="Last updated"
        value={observedTime(board.generatedAt, state.tick, format)}
      />
    </footer>
  );
}

function StripCell({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  tone?: 'good' | 'warn' | 'dim';
}) {
  return (
    <div className={`df-strip-cell${tone ? ` is-${tone}` : ''}`}>
      <span className="df-strip-icon" aria-hidden>
        {icon}
      </span>
      <span className="df-strip-copy">
        <span className="df-strip-label">{label}</span>
        <span className="df-strip-value">{value}</span>
      </span>
    </div>
  );
}
